import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { useQuery } from 'convex/react'
import { useEffect, useMemo, useRef } from 'react'

import { projectGoalTree } from '@/hooks/use-offline-mutations'
import { readSnapshot, writeSnapshot } from '@/lib/offline-cache'
import { useOfflineStore } from '@/store/offline.store'

/**
 * getGoalTree with an offline snapshot fallback and the pending queue projected
 * on top, so completions/status changes made offline persist across reloads.
 */
export function useGoalTree(goalId: Id<'nodes'>) {
  const live = useQuery(api.nodes.getGoalTree, { goalId })
  const queue = useOfflineStore((s) => s.queue)

  const cacheRef = useRef<typeof live>(undefined)
  if (cacheRef.current === undefined) {
    cacheRef.current = readSnapshot(`goaltree:${goalId}`) ?? undefined
  }
  useEffect(() => {
    if (live !== undefined && live !== null) {
      writeSnapshot(`goaltree:${goalId}`, live)
      cacheRef.current = live
    }
  }, [live, goalId])

  const base = live ?? cacheRef.current
  return useMemo(() => (base == null ? base : projectGoalTree(base, queue)), [base, queue])
}
